import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router';

import styled from 'styled-components';

import { getCommunityPhotos } from 'apis/CommunityAPI';
import { hasPermissionOrAdmin } from 'helpers/permissions';
import { PHOTOS_PERMISSIONS } from 'permissions/photos';
import { useAppSelector } from 'store/hook';

import PhotosEditModal from './edit/PhotosEditModal';
import ImageWrapper from './ImageWrapper';
import { PhotosContext } from './PhotosContext';
import PhotosFilter from './PhotosFilter';
import PhotosPreview from './PhotosPreview';
import { FILE_SOURCE } from '../../../../constants';
import Header from '../../common/header/Header';

const Container = styled.div`
  position: relative;
  min-height: 240px;
`;

const Content = styled.div`
  margin-top: 15px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Photos = () => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const hasPricingDataAccess = Boolean(currentUser?.isSuperuser || currentUser?.feature_flags?.view_pricing_data);

  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(0);
  const [openPreviewPhotosModal, setOpenPreviewPhotosModal] = useState<boolean>(false);
  const [photos, setPhotos] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  const { competitorId } = useParams();
  const { communityId } = useParams();

  const id = Number(competitorId || communityId);

  const { isSuccess, refetch } = useQuery({
    queryKey: ['community', 'photos', id, selectedCategory],
    queryFn: () => getCommunityPhotos(id, { image_category: selectedCategory || null, source: [FILE_SOURCE.MEDIA] }),
    onSuccess: (data) => {
      setPhotos(data);
    },
    enabled: Boolean(id),
  });

  const previewHandler = (index: number) => {
    setSelectedPhotoIndex(index);
    setOpenPreviewPhotosModal(true);
  };

  const canEditMedia = hasPermissionOrAdmin(currentUser, [PHOTOS_PERMISSIONS.CHANGE]);
  const showEditButton = hasPricingDataAccess && !competitorId && isSuccess && canEditMedia;

  return (
    <PhotosContext.Provider
      value={{
        selectedPhotoIndex,
        openPreviewPhotosModal,
        photos,
        selectedCategory,
        setSelectedCategory,
        setSelectedPhotoIndex,
        setOpenPreviewPhotosModal,
        setPhotos,
      }}
    >
      <Container>
        <Header
          title="Photos"
          controls={
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PhotosEditModal
                type={'button'}
                show={showEditButton}
                buttonDataTestId="cd-edit-photos-btn"
                onSuccess={refetch}
              />
              <PhotosFilter />
            </div>
          }
        />
        <Content>
          {photos?.map((item: any, index: number) => (
            <ImageWrapper key={item.id} item={item} onClick={() => previewHandler(index)} />
          ))}
          {!photos?.length && (
            <PhotosEditModal
              type={'link'}
              show={showEditButton}
              linkDataTestId="cd-add-photos-link"
              onSuccess={refetch}
            />
          )}
        </Content>
        <PhotosPreview />
      </Container>
    </PhotosContext.Provider>
  );
};

export default Photos;
